/**
 * What the platform asks of a password, in one place.
 *
 * These were written out by hand at every surface that takes one: the sign-up form spells
 * out five rules, the reset form four (it forgot sequential digits), and the profile's
 * Change password dialog none at all — so an account created under five rules could be
 * changed to a single letter, and the only thing that said no was the server, after a
 * round trip, in a sentence the dialog printed at the bottom.
 *
 * The server is still the authority: it runs a configurable policy that also knows about
 * password history and the account's own name, and it can refuse a password that satisfies
 * every rule here. This is the baseline the product has always stated at sign-up, checked
 * where the person is typing so that the common refusals never need a round trip.
 */

/** One requirement, phrased the way it is shown beside the field. */
export interface IPasswordRequirement {
  /** Stable key for React and for tests. */
  id: string;
  /** Four or five words, scannable in a column — not a sentence. */
  label: string;
  /** The sentence form, for a validator that reports one failure at a time. */
  message: string;
  isMet: (password: string) => boolean;
}

/** Four or more digits in a run, ascending — 1234, 5678, and the 7890 wrap. */
const SEQUENTIAL_DIGITS: RegExp = /(0123|1234|2345|3456|4567|5678|6789|7890)/;

export const PASSWORD_REQUIREMENTS: IPasswordRequirement[] = [
  {
    id: "length",
    label: "8 characters or more",
    message: "Password length must be at least 8",
    isMet: (password: string): boolean => password.length >= 8
  },
  {
    id: "lowercase",
    label: "A lowercase letter",
    message: "Password must contain at least 1 lowercase character",
    isMet: (password: string): boolean => /[a-z]/.test(password)
  },
  {
    id: "uppercase",
    label: "An uppercase letter",
    message: "Password must contain at least 1 uppercase character",
    isMet: (password: string): boolean => /[A-Z]/.test(password)
  },
  {
    id: "symbol",
    label: "A symbol",
    message: "Password must contain at least 1 special character",
    isMet: (password: string): boolean => /[!@#$%^&*(),.?":{}|<>\-_]/.test(password)
  },
  {
    id: "no-run",
    label: "No run like 1234",
    message: "Password must not contain more than 3 sequential digits",
    isMet: (password: string): boolean => !SEQUENTIAL_DIGITS.test(password)
  }
];

/** The requirements this password does not yet satisfy, in the order they are shown. */
export function getUnmetPasswordRequirements(password: string): IPasswordRequirement[] {
  return PASSWORD_REQUIREMENTS.filter((requirement: IPasswordRequirement): boolean => !requirement.isMet(password));
}

export function isPasswordAcceptable(password: string): boolean {
  return getUnmetPasswordRequirements(password).length === 0;
}
